import type { Grid } from '@react-three/drei';
import CameraControls from 'camera-controls';
import { ComponentProps } from 'react';

import { color } from '@/features/editor/lib/colors';

export const CONSTANTS = {
  softwareName: 'spaiderdemo',
  apiOfflineOverlayDelayS: 5 * 60,
  projects: {
    defaultProjectId: '69e1fcea7bd08b0a3a1e170e',
    acceptFiles: '.json,.stl,.png',
    concurrentDownload: 5,
    localProjectsPrefix: 'local-',
  },
  auth: {
    defaultLandingPath: '/',
  },
  tasks: {
    pollingRateMs: 2500,
    softwareName: 'spaiderdemo',
    projectSetupType: 'project-setup',
    runNN: 'run-nn',
  },
  camera: {
    frustumSize: 180,
    position: [0, 0, 10],
    defaultTarget: [0.5, 0, 0],
    defaultZoom: 130,
    nnresultZoom: 110,
    nnDefaultTarget: [0.6, 0, 0],
    /**
     * Multiplied to the zoom required to fix
     * any bbox allows to have more or less space around
     * the calculated bbox
     */
    zoomFactor: 1.3 as number,
    near: 0.01,
    far: 50,
    /**
     * Enable the camera controls to zoom always around the
     * position of the mouse in the scene
     */
    dollyToCursor: true as boolean,
  },
  controls: {
    smoothTime: 0.2,
    draggingSmoothTime: 0.1,
    truckSpeed: 0.8,
    defaultMouseMapping: {
      left: CameraControls.ACTION.TRUCK,
      right: CameraControls.ACTION.TRUCK,
      wheel: CameraControls.ACTION.ZOOM,
      middle: CameraControls.ACTION.TRUCK,
    },
    defaultTouchMapping: {
      one: CameraControls.ACTION.NONE,
      two: CameraControls.ACTION.NONE,
      three: CameraControls.ACTION.NONE,
    },
    /**
     * Keep the camera position X always perpendicular
     * to the target X
     */
    keepPerpendicular: true,
  },
  colors: {
    defaultCube: color('#bae6fd'),
    defaultColor: color('#0ea5e9'),
    edgesColor: color('#c4c4c4'),
    selectedEdgesColor: color('#ffffff'),
    cameraTargetColor: color('#eaeaea'),
  },
  ground: {
    cellSize: 1,
    cellThickness: 1.5,
    cellColor: '#0369a1',
    sectionSize: 0.1,
    sectionColor: '#0c4a6e',
    followCamera: false,
    infiniteGrid: false,
    rotation: [Math.PI / 2, 0, 0],
    position: [0, 0, 0],
    args: [5, 5],
  } as Partial<ComponentProps<typeof Grid>>,
  scene: {
    up: [0, 1, 1],
    scaleFactor: 1,
    ambientLightIntensity: 0.5,
    light: {
      color: 'rgb(255,253,250)',
      intensity: 0.6,
      position: [40, 40, 30],
    },
    lightBackground: 'rgb(235, 235, 235)',
    darkBackground: 'rgb(39, 43, 44)',
    centerByTopRight: false,
  },
  target: {
    /**
     * Enables the display of the current target
     * of the camera
     */
    enabled: false,
    size: 0.2,
    lineFactor: 1.5,
    opacity: 0.8,
    /**
     * How much time the UI should wait before
     * actually updating the camera target
     */
    debounceUpdateMs: 300,
  },
  viewerFeatures: {
    title: {
      enabled: true,
      /**
       * Always shows a translated title if populated
       */
      defaultTitle: 'customBaseNames.foil' as string | null,
    },
    /**
     * Enable or disable the possibility
     * to change the visibility of the objects in the
     * scene. Disable if visibility is controlled
     * by other mechanism (eg: visible only the selected geometry)
     */
    toggleObjectsVisibility: {
      enabled: false,
    },
    /**
     * Toggle gizmo helper
     */
    gizmo: {
      enabled: false,
    },
  },
  custom: {
    variants: ['streamlines', 'speed', 'pressure'],
    nnresult: {
      zIndex: 1,
      foilColor: '#d4d4d8',
    },
    foil: {
      width: 2,
      rotationPoint: {
        args: [0.01, 20] as [number, number],
        color: color('#71717a'),
      },
      bezierLines: {
        dashSize: 0.02,
        dashGap: 0.02,
      },
      joinLines: {
        dashSize: 0.02,
        dashGap: 0.02,
        color: color('#71717a'),
      },
      spanLines: {
        width: 0.5,
        color: color('#fef9c3'),
      },
      controlPoints: {
        size: 1,
        fixedColor: color('#e0f2fe'),
        editableColor: color('#0ea5e9'),
      },
      zIndexes: {
        curve: 2,
        line: 2.001,
        controlPoints: 2.002,
      },
    },
  },
  searchParams: {
    sheetPage: 'sp',
    includeBBox: 'bbi',
    previewNN: 'pnn',
  },
  supportEmail: 'support@spaider.ai',
} as const;
